/**
 * Route tree.
 *
 * Defined in code rather than by file convention: the tree is small enough that one file
 * showing every route is easier to reason about than a directory of implicitly-named files.
 */
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  useRouterState,
} from '@tanstack/react-router'

import { AppShell } from '@/components/shell/AppShell'
import { AuthEntry } from '@/routes/auth/AuthEntry'
import { BillingPage } from '@/routes/BillingPage'
import { HomePage } from '@/routes/HomePage'
import { PricingPage } from '@/routes/PricingPage'
import { PrivacyPolicyPage } from '@/routes/legal/PrivacyPolicyPage'
import { TermsPage } from '@/routes/legal/TermsPage'
import { BenchmarkPage } from '@/routes/BenchmarkPage'
import { BenchmarkRunPage } from '@/routes/BenchmarkRunPage'
import { ChatPage } from '@/routes/ChatPage'
import { ExecutionsPage } from '@/routes/ExecutionsPage'
import { MetricsPage } from '@/routes/MetricsPage'
import { NotFoundPage } from '@/routes/NotFoundPage'
import { ProfilePage } from '@/routes/ProfilePage'
import { RepositoriesPage } from '@/routes/RepositoriesPage'
import { RepositoryDetailPage } from '@/routes/RepositoryDetailPage'
import { SettingsPage } from '@/routes/SettingsPage'

/**
 * Routes that must render without a session.
 *
 * The privacy policy and terms are opened by people who are not signed in — including
 * Google's OAuth reviewer, who fails an application whose policy URL redirects to a login
 * screen. The landing and pricing pages are the same case for a different reason: they are
 * what someone reads *before* deciding to create an account, and pricing behind a login is
 * a dark pattern in a product that is asking to be paid. So the root decides: these render
 * bare, everything else goes through the application shell and its sign-in gate.
 */
const PUBLIC_PATHS = new Set(['/', '/pricing', '/signin', '/signup', '/privacy', '/terms'])

function RootShell() {
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  if (PUBLIC_PATHS.has(pathname)) return <Outlet />
  return <AppShell />
}

const rootRoute = createRootRoute({
  component: RootShell,
  notFoundComponent: NotFoundPage,
})

// The front door: the landing page for a visitor, and a redirect into the product for
// anyone whose session is still good. HomePage makes that call, because it is the only
// component here that knows whether there is a user.
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: HomePage,
})

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signin',
  component: () => <AuthEntry mode="signin" />,
})

const signUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signup',
  component: () => <AuthEntry mode="signup" />,
})

const pricingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pricing',
  component: PricingPage,
})

const billingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/billing',
  component: BillingPage,
})

const chatRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/chat',
  component: ChatPage,
})

const repositoriesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/repositories',
  component: RepositoriesPage,
})

const repositoryDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/repositories/$owner/$repo',
  component: RepositoryDetailPage,
})

const executionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/executions',
  component: ExecutionsPage,
})

const metricsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/metrics',
  component: MetricsPage,
})

const benchmarkRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/benchmark',
  component: BenchmarkPage,
})

const benchmarkRunRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/benchmark/$runUid',
  component: BenchmarkRunPage,
})

const profileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/profile',
  component: ProfilePage,
})

const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/privacy',
  component: PrivacyPolicyPage,
})

const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/terms',
  component: TermsPage,
})

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  component: SettingsPage,
})

const routeTree = rootRoute.addChildren([
  indexRoute,
  signInRoute,
  signUpRoute,
  pricingRoute,
  billingRoute,
  chatRoute,
  repositoriesRoute,
  repositoryDetailRoute,
  executionsRoute,
  metricsRoute,
  benchmarkRoute,
  benchmarkRunRoute,
  profileRoute,
  settingsRoute,
  privacyRoute,
  termsRoute,
])

export const router = createRouter({ routeTree, defaultPreload: 'intent' })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
